import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft, Check, Loader2, ExternalLink, AlertCircle, RefreshCcw, PhoneCall,
} from 'lucide-react';
import FFLoader from '../components/FFLoader';
import { formsApi } from '../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => {
  const token = localStorage.getItem('ff_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Per-form CallRail configuration page.
 *
 * Two-step UX: paste API token → Test lists your CallRail Accounts →
 * pick one → Save. Submissions hit
 * POST /v3/a/{account_id}/form_submissions.json with attribution
 * (UTMs, gclid/fbclid/msclkid, referrer, landing page) forwarded so
 * CallRail's reporting unifies form leads with phone calls.
 */
export default function CallRailIntegrationPage() {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const formList = await formsApi.getAll();
        const formsArray = Array.isArray(formList) ? formList : (formList?.forms || []);
        const decorated = formsArray.map((f) => {
          const c = (f?.settings?.callrail) || {};
          return {
            ...f,
            _c: {
              tokenInput: '',
              enabled: !!c.enabled,
              configured: !!(c.api_token && c.account_id),
              maskedToken: c.api_token ? `…${c.api_token.slice(-6)}` : null,
              accountId: c.account_id ? String(c.account_id) : '',
              accountName: c.account_name || null,
              accounts: [],
              lastError: c.last_error || null,
              saving: false,
              testing: false,
            },
          };
        });
        setForms(decorated);
      } catch (e) {
        console.error(e);
        toast.error('Failed to load CallRail settings');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const patchForm = (id, patch) => setForms((prev) => prev.map((f) =>
    f.id === id ? { ...f, _c: { ...f._c, ...patch } } : f
  ));

  const handleTest = async (form) => {
    const api_token = (form._c.tokenInput || '').trim();
    if (!api_token) { toast.error('Paste your CallRail API token first'); return; }
    patchForm(form.id, { testing: true });
    try {
      const { data } = await axios.post(
        `${API}/api/callrail/test`,
        { api_token },
        { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
      );
      const accounts = data.accounts || [];
      toast.success(`Connected — found ${accounts.length} account${accounts.length === 1 ? '' : 's'}`);
      patchForm(form.id, {
        accounts,
        accountId: form._c.accountId || (accounts[0]?.id ? String(accounts[0].id) : ''),
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not reach CallRail');
    } finally {
      patchForm(form.id, { testing: false });
    }
  };

  const handleSave = async (form) => {
    const api_token = (form._c.tokenInput || '').trim();
    const account_id = form._c.accountId || null;
    if (!api_token) { toast.error('Paste your CallRail API token first'); return; }
    if (!account_id) { toast.error('Pick an account first (click Test)'); return; }
    patchForm(form.id, { saving: true });
    try {
      const { data } = await axios.put(
        `${API}/api/callrail/forms/${form.id}`,
        { enabled: true, api_token, account_id },
        { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
      );
      toast.success(`Connected — submissions will sync to "${data.account_name}"`);
      patchForm(form.id, {
        enabled: true,
        configured: true,
        maskedToken: data.api_token_masked || null,
        accountId: data.account_id ? String(data.account_id) : form._c.accountId,
        accountName: data.account_name || null,
        tokenInput: '',
        lastError: null,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save CallRail connection');
    } finally {
      patchForm(form.id, { saving: false });
    }
  };

  const handleDisconnect = async (form) => {
    if (!window.confirm('Disconnect CallRail? New submissions will no longer sync.')) return;
    patchForm(form.id, { saving: true });
    try {
      await axios.put(
        `${API}/api/callrail/forms/${form.id}`,
        { enabled: false },
        { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
      );
      toast.success('Disconnected');
      patchForm(form.id, {
        enabled: false, configured: false, maskedToken: null,
        accountId: '', accountName: null, accounts: [],
        tokenInput: '', lastError: null,
      });
    } catch (e) {
      toast.error('Failed to disconnect');
    } finally {
      patchForm(form.id, { saving: false });
    }
  };

  const handleToggle = async (form, checked) => {
    if (!form._c.configured) {
      toast.error('Save a connection first, then toggle it on.');
      return;
    }
    patchForm(form.id, { saving: true });
    try {
      await axios.put(
        `${API}/api/callrail/forms/${form.id}`,
        { enabled: checked },
        { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
      );
      patchForm(form.id, { enabled: checked });
      toast.success(checked ? 'CallRail sync resumed' : 'CallRail sync paused');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update');
      patchForm(form.id, { enabled: !checked });
    } finally {
      patchForm(form.id, { saving: false });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <FFLoader size={48} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="callrail-page">
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-4">
          <Link to="/integrations" className="p-2 hover:bg-zinc-100 rounded-lg">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-[#0091EA] rounded-lg flex items-center justify-center">
              <PhoneCall className="w-4 h-4 text-white" />
            </div>
            <h1 className="text-xl font-bold text-zinc-900">CallRail</h1>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        <Card>
          <CardHeader><CardTitle className="text-base">How it works</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <ol className="text-sm text-zinc-700 space-y-2 list-decimal list-inside">
              <li>
                In CallRail, open{' '}
                <a href="https://app.callrail.com/account/api-keys" target="_blank" rel="noopener noreferrer"
                   className="underline inline-flex items-center gap-0.5">
                  Account → Integrations → API Keys <ExternalLink className="w-3 h-3" />
                </a>{' '}
                and click <strong>Create API Key</strong>.
              </li>
              <li>Copy the token (32-character alphanumeric string).</li>
              <li>Paste it below, click <strong>Test</strong> to load your accounts, pick one, and click <strong>Save</strong>.</li>
              <li>
                Submissions become CallRail <em>Form Submissions</em> with attribution
                (UTM, gclid, fbclid, msclkid, referrer, landing page) forwarded — so they
                unify with calls in your CallRail reports.
              </li>
            </ol>
            <p className="text-xs text-zinc-500">
              Forms without an email or phone field are skipped (CallRail requires at least one
              identifier to match a visitor session).
            </p>
          </CardContent>
        </Card>

        <section className="space-y-3" data-testid="forms-section">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-zinc-900">Your forms</h2>
            <span className="text-xs text-zinc-500">{forms.length} {forms.length === 1 ? 'form' : 'forms'}</span>
          </div>

          {forms.length === 0 ? (
            <Card><CardContent className="py-10 text-center text-sm text-zinc-500">
              No forms yet. <Link to="/dashboard" className="text-zinc-900 underline">Go to dashboard</Link>.
            </CardContent></Card>
          ) : (
            <div className="space-y-3">
              {forms.map((form) => (
                <Card key={form.id} data-testid={`form-row-${form.id}`}>
                  <CardContent className="p-5 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-medium text-zinc-900 truncate">{form.title || 'Untitled form'}</p>
                        {form._c.configured ? (
                          <p className="text-xs text-zinc-500 mt-0.5">
                            Connected to account <strong>{form._c.accountName}</strong>
                            {' '}· token <code className="px-1 bg-zinc-100 rounded">{form._c.maskedToken}</code>
                          </p>
                        ) : (
                          <p className="text-xs text-zinc-400 mt-0.5">Not connected</p>
                        )}
                        {form._c.lastError && (
                          <p className="text-xs text-red-600 mt-1 inline-flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" /> Last sync failed: {form._c.lastError}
                          </p>
                        )}
                      </div>
                      {form._c.configured && (
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-xs text-zinc-500">{form._c.enabled ? 'Active' : 'Paused'}</span>
                          <Switch checked={form._c.enabled}
                                  onCheckedChange={(c) => handleToggle(form, c)}
                                  data-testid={`callrail-toggle-${form.id}`} />
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs text-zinc-500">
                          CallRail API token{form._c.configured ? ' (paste a new one to replace)' : ''}
                        </Label>
                        <Input
                          placeholder="abc123…"
                          value={form._c.tokenInput}
                          onChange={(e) => patchForm(form.id, { tokenInput: e.target.value })}
                          data-testid={`callrail-token-${form.id}`}
                          className="font-mono text-xs"
                          type="password"
                          autoComplete="off"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-zinc-500">Account</Label>
                        <Select value={form._c.accountId || undefined}
                                onValueChange={(v) => patchForm(form.id, { accountId: v })}
                                disabled={form._c.accounts.length === 0}>
                          <SelectTrigger className="text-xs" data-testid={`callrail-account-${form.id}`}>
                            <SelectValue placeholder={
                              form._c.accounts.length === 0 ? 'Click Test to load accounts' : 'Pick an account'
                            } />
                          </SelectTrigger>
                          <SelectContent>
                            {form._c.accounts.map((a) => (
                              <SelectItem key={a.id} value={String(a.id)} className="text-xs">
                                {a.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" size="sm"
                              onClick={() => handleTest(form)}
                              disabled={form._c.testing || form._c.saving}
                              data-testid={`callrail-test-${form.id}`}>
                        {form._c.testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4 mr-1" />}
                        Test
                      </Button>
                      <Button size="sm"
                              onClick={() => handleSave(form)}
                              disabled={form._c.saving || form._c.testing}
                              data-testid={`callrail-save-${form.id}`}>
                        {form._c.saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                        {form._c.configured ? 'Replace' : 'Save'}
                      </Button>
                      {form._c.configured && (
                        <Button variant="ghost" size="sm"
                                onClick={() => handleDisconnect(form)}
                                disabled={form._c.saving}
                                data-testid={`callrail-disconnect-${form.id}`}>
                          Disconnect
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
